import { useState, useEffect } from "react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { History, Calendar, Eye } from "lucide-react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { fr, es, enUS } from "date-fns/locale";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import type { AIAnalysisResult } from "@/hooks/useAIAnalysis";

interface AnalysisHistoryItem {
  id: string;
  created_at: string;
  recommendations: AIAnalysisResult;
}

interface AnalysisHistoryProps {
  onSelectAnalysis: (analysis: AIAnalysisResult, date: string) => void;
}

const AnalysisHistory = ({ onSelectAnalysis }: AnalysisHistoryProps) => {
  const { t, i18n } = useTranslation('ai-analysis');
  const [analyses, setAnalyses] = useState<AnalysisHistoryItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const dateLocale = i18n.language === 'fr' ? fr : i18n.language === 'es' ? es : enUS;

  useEffect(() => {
    if (open) {
      loadAnalyses();
    }
  }, [open]);

  const loadAnalyses = async () => {
    try {
      setIsLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from("ai_analysis_results")
        .select("id, created_at, recommendations")
        .eq("user_id", user.id)
        .eq("analysis_type", "performance_analysis")
        .order("created_at", { ascending: false })
        .limit(20);

      if (error) throw error;
      setAnalyses((data || []) as unknown as AnalysisHistoryItem[]);
    } catch (error) {
      console.error('Error loading analyses:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSelectAnalysis = (analysis: AIAnalysisResult, date: string) => {
    onSelectAnalysis(analysis, date);
    setOpen(false);
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetTrigger asChild>
        <Button variant="outline">
          <History className="h-4 w-4 mr-2" />
          {t('history.button')}
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[400px] sm:w-[540px]">
        <SheetHeader>
          <SheetTitle>{t('history.title')}</SheetTitle>
          <SheetDescription>
            {t('history.description')}
          </SheetDescription>
        </SheetHeader>
        <ScrollArea className="h-[calc(100vh-120px)] mt-6 pr-4">
          {isLoading ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('common:loading')}
            </div>
          ) : analyses.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {t('history.empty')}
            </div>
          ) : (
            <div className="space-y-3">
              {analyses.map((item) => (
                <div
                  key={item.id}
                  className="border rounded-lg p-4 hover:bg-accent transition-colors cursor-pointer"
                  onClick={() => handleSelectAnalysis(item.recommendations, item.created_at)}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm font-medium">
                        {format(new Date(item.created_at), 'PPP', { locale: dateLocale })}
                      </span>
                    </div>
                    <Badge variant="secondary">
                      {item.recommendations.globalAnalysis.overallScore}/100
                    </Badge>
                  </div>
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                    {item.recommendations.globalAnalysis.summary}
                  </p>
                  <Button
                    size="sm"
                    variant="ghost"
                    className="w-full"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleSelectAnalysis(item.recommendations, item.created_at);
                    }}
                  >
                    <Eye className="h-4 w-4 mr-2" />
                    {t('history.view')}
                  </Button>
                </div>
              ))}
            </div>
          )}
        </ScrollArea>
      </SheetContent>
    </Sheet>
  );
};

export default AnalysisHistory;
